import { defineStore } from 'pinia'
import { login, LoginRequest } from '../services/login'
import { TOKEN_KEY } from '../utils/request'
import { FINGER_KEY } from '../utils/fingerprint'
import router from '../router'

export interface UserState {
  isAuthenticated: boolean
  username: string
  token: string | null
  finger: string | null
  is2FAEnabled: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): UserState => {
    // 检查本地存储中是否有token，使用统一的TOKEN_KEY
    const token = localStorage.getItem(TOKEN_KEY)
    const finger = localStorage.getItem(FINGER_KEY)

    return {
      isAuthenticated: !!token,
      username: '',
      token,
      finger,
      is2FAEnabled: false,
    }
  },

  actions: {
    // 登录方法
    async login(loginData: LoginRequest) {
      try {
        const response = await login.login(loginData)

        // 检查是否需要TOTP验证
        if (response.needTotp) {
          // 如果需要TOTP验证，直接返回响应，让上层处理
          return response
        }

        // 正常登录流程
        if (response.token) {
          this.token = response.token
          this.finger = loginData.finger
          this.isAuthenticated = true
          this.username = loginData.username
        }

        return response
      } catch (error) {
        // 确保token和认证状态清除
        this.token = null
        this.isAuthenticated = false
        throw error
      }
    },

    // 注销方法
    logout() {
      // 先清除状态
      this.token = null
      this.finger = null
      this.isAuthenticated = false
      this.username = ''
      this.is2FAEnabled = false

      // 调用登出服务，但不执行重定向
      login.logout()

      // 使用Vue Router导航而不是直接修改location
      // 先验证router是否已初始化
      try {
        // 使用延迟执行，确保当前组件的操作已完成
        setTimeout(() => {
          router.push({ name: 'login' })
        }, 100)
      } catch (e) {
        // 如果Router失败，则使用传统方式重定向
        login.redirectToLogin()
      }
    },

    // 从存储中恢复认证状态
    restoreAuth() {
      // 使用统一的TOKEN_KEY
      const token = localStorage.getItem(TOKEN_KEY)
      const finger = localStorage.getItem(FINGER_KEY)

      if (token) {
        this.token = token
        this.finger = finger
        this.isAuthenticated = true
      } else {
        this.isAuthenticated = false
        this.token = null
        this.finger = null
      }
    },
  },

  getters: {
    // 获取认证状态
    getAuthStatus(): boolean {
      return this.isAuthenticated
    },

    // 获取用户名
    getUsername(): string {
      return this.username
    },
  },
})
